import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "assets";
import styles from "./styles.module.scss";

const capitalizeFirstWord = (str: string) => {
  if (!str) return "";
  const s = str.replace(/-/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const capitalizeWords = (str: string) =>
  str.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const isIdSegment = (s: string) => {
  return (
    /^\d+$/.test(s) || // numeric IDs
    /^[0-9a-fA-F]{24}$/.test(s) || // mongo ObjectId
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
      s
    )
  );
};

const Breadcrumbs: React.FC = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const raw = pathname.split("/").filter(Boolean);
  const SKIP = new Set(["employer"]);

  // Extract URL parameters
  const urlParams = new URLSearchParams(search);
  const fromParam = urlParams.get('from');
  const retCVAnalysisParam = urlParams.get('retCVAnalysis');
  // Return URL to the filtered review-talents page (set when a report is opened in a new tab).
  const retReviewParam = urlParams.get('retReview');

  const keptIndices = raw.reduce<number[]>((acc, seg, idx) => {
    if (!SKIP.has(seg.toLowerCase()) && !isIdSegment(seg)) acc.push(idx);
    return acc;
  }, []);

  if (keptIndices.length === 0) return null;

  // Show back if more than 1 segment in breadcrumb
  const showBack = keptIndices.length > 1;

  return (
    <section className="overflow-auto scrollbar-none w-full">
      <section className={styles.mainSection}>
        {showBack && (
          <span
            className={styles.back}
            onClick={() => (retReviewParam ? navigate(retReviewParam) : navigate(-1))}
          >
            <ArrowLeft />
          </span>
        )}
        {keptIndices.map((rawIdx, i) => {
          const name = raw[rawIdx];
          const isLast = i === keptIndices.length - 1;

          // Include all segments up to the current
          let toSegments = [...raw.slice(0, rawIdx + 1)];

          // Add consecutive ID segments that follow, but skip IDs for "manage-jobs"
          if (name !== "manage-jobs") {
            for (let j = rawIdx + 1; j < raw.length; j++) {
              if (isIdSegment(raw[j])) {
                toSegments.push(raw[j]);
              } else {
                break;
              }
            }
          }

          const to = "/" + toSegments.join("/");

          // Check if this is "review-talent". It normally isn't clickable since it needs an ID,
          // but when a report carries a retReview return URL we can navigate back to the exact
          // filtered list, so treat it as a normal link in that case.
          const isReviewTalent = name === "review-talent" && pathname.includes("/review-talent/");
          const reviewTalentClickable = isReviewTalent && !isLast && !!retReviewParam;

          // Check if this is "cv-analysis" - navigate to manage-jobs with cv-analysis tab
          const isCVAnalysis = name === "cv-analysis";
          // Prefer retCVAnalysis param (preserves filter state) over hardcoded default
          const cvAnalysisPath = retCVAnalysisParam
            ?? "/employer/manage-jobs?tab=cv-analysis&cvFilter=all&cvPage=1";

          // If clicking on manage-jobs and we have a from param with manage-jobs URL, use that
          const isManageJobs = name === "manage-jobs";
          const clickPath = isCVAnalysis
            ? cvAnalysisPath
            : reviewTalentClickable
            ? (retReviewParam as string)
            : (isManageJobs && fromParam && fromParam.includes('/manage-jobs') ? fromParam : to);

          // review-talent without a return URL stays non-clickable; everything else falls through.
          const renderAsCurrent = isLast || (isReviewTalent && !reviewTalentClickable);

          return (
            <span key={to} className={styles.crumbWrapper}>
              {i > 0 && <span className={styles.separator}>/</span>}
              {renderAsCurrent ? (
                <span className={isReviewTalent && !isLast ? `${styles.current} cursor-not-allowed opacity-60 whitespace-nowrap` : `${styles.current} whitespace-nowrap`}>
                  {isLast ? capitalizeWords(name) : capitalizeFirstWord(name)}
                </span>
              ) : (
                <span className={`whitespace-nowrap ${styles.link}`} onClick={() => navigate(clickPath)}>
                  {capitalizeFirstWord(name)}
                </span>
              )}
            </span>
          );
        })}
      </section>
    </section>

  );
};

export { Breadcrumbs };